import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './About.css';

// ==========================================
// 1. TIMELINE DATA FOR MILESTONES
// ==========================================

// ==========================================
const timelineData = {
  "2020": {
    events: [
      "Successfully launched a comprehensive suite of advanced mobile banking features, introducing biometric security, real-time transaction tracking, and intuitive wealth management tools directly to our users' smartphones.",
      "Achieved a historic milestone by surpassing 1 million active digital users, reflecting widespread customer trust and the rapid adoption of our newly optimized, user-first digital ecosystem."
    ],
    awards: [
      { org: "GLOBAL FINANCE", title: "Best Consumer Digital Bank 2020" },
      { org: "ASIAN BANKER", title: "Excellence in Mobile Banking" },
      { org: "FINTECH AWARDS", title: "Outstanding Digital Transformation" }
    ]
  },
  "2019": {
    events: [
      "Revolutionized our client service experience by introducing AI-powered customer support, enabling 24/7 instant query resolution and personalized financial assistance.",
      "Successfully expanded our physical footprint into 3 new high-growth regions, bridging the gap between cutting-edge digital services and dedicated, in-person financial advisory."
    ],
    awards: [
      { org: "RETAIL BANKER INTERNATIONAL", title: "Excellence in Customer Service" },
      { org: "BANKING TECH AWARDS", title: "Best Use of AI in Financial Services" },
      { org: "WORLD FINANCE", title: "Best Branch Strategy Implementation" }
    ]
  },
  "2018": {
    events: [
      "Pioneered the transition to modern transactional convenience by becoming the first local bank to introduce secure, high-speed contactless payment technology across all issued debit and credit cards."
    ],
    awards: [
      { org: "CITI BANK", title: "Innovation in Payments" },
      { org: "PAYMENTS AWARDS", title: "Best Contactless Initiative" },
      { org: "CARD & PAYMENTS ASIA", title: "Leading FinTech Trailblazer Award" }
    ]
  },
  "2017": {
    events: [
      "Demonstrated our commitment to sustainable business practices by becoming the first Mauritian bank to officially join the Global Reporting Initiative (GRI) Community.",
      "Drastically reduced friction in client acquisition by launching a proprietary Customer On-boarding App, allowing users to experience secure, instant account opening from anywhere."
    ],
    awards: [
      { org: "CITI BANK", title: "Straight-Through processing (STP) Excellence Award" },
      { org: "MARCOM AWARDS", title: "Platinum Winner for ‘The Believers’ Campaign" },
      { org: "BURJ CEO AWARDS", title: "Most Innovative Bank in Mauritius" },
      { org: "GLOBAL BRANDS MAGAZINE", title: "Most Innovative Treasury Services" },
      { org: "GLOBAL BRANDS MAGAZINE", title: "Best Banking Brand in Mauritius" },
      { org: "GLOBAL BRANDS MAGAZINE", title: "Best Credit Card Rewards Program" }
    ]
  },
  "2016": {
    events: [
      "Launched our highly anticipated first rewards credit card, offering unparalleled benefits, cashback, and exclusive lifestyle privileges tailored to our customers' spending habits."
    ],
    awards: [
      { org: "GLOBAL BRANDS MAGAZINE", title: "Consumer Choice: Best Rewards Card" },
      { org: "FINANCIAL INSIGHTS", title: "Outstanding Card Product Design" }
    ]
  },
  "2015": {
    events: [
      "Executed a massive, seamless upgrade to our core banking system, significantly enhancing transaction processing speeds, enterprise-level security, and overall digital scalability."
    ],
    awards: [
      { org: "IT EXCELLENCE AWARDS", title: "Best IT Infrastructure Transformation" },
      { org: "CORE BANKING SUMMIT", title: "Implementation Project of the Year" }
    ]
  },
  "2014 and before": {
    events: [
      "Laid the critical groundwork for our institution, establishing a robust operational foundation, securing necessary regulatory licenses, and setting the early strategic milestones that would define our future growth."
    ],
    awards: [
      { org: "AFRICAN BANKING AWARDS", title: "Emerging Bank of the Year" },
      { org: "FINANCE MONTHLY", title: "Best New Financial Institution" }
    ]
  }
};

// ==========================================
// 2. MILESTONES COMPONENT
// ==========================================
const Milestones = () => {
  const [activeYear, setActiveYear] = useState("2017");
  const currentData = timelineData[activeYear];

  return (
    <section className="milestones-section">
      <div className="milestones-container">
        {/* Header */}
        <div className="milestones-header">
          <span className="milestones-overline">Recognition</span>
          <h2 className="milestones-title">Milestones and Accolades</h2>
        </div>

        <div className="milestones-grid">
          {/* Left Column: Timeline Navigation */}
          <div className="timeline-nav">
            {Object.keys(timelineData).reverse().map((year) => (
              <button
                key={year}
                className={`timeline-nav-btn ${activeYear === year ? 'active' : ''}`}
                onClick={() => setActiveYear(year)}
              >
                {year}
              </button>
            ))}
          </div>

          {/* Middle Column: Events Content */}
          <div className="timeline-content">
            <h3 className="active-year-title">{activeYear}</h3>
            <ul className="events-list">
              {currentData.events.map((event, index) => (
                <li key={index}>
                  <span className="dash">—</span> {event}
                </li>
              ))}
            </ul>
          </div>

          {/* Right Column: Awards Card */}
          <div className="awards-column">
            <h3 className="awards-header-text">Awards</h3>
            <div className="awards-card">
              {/* SVG Trophy Icon overlapping top right */}
              <div className="trophy-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#d4b281" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 21h8M12 17v4M7 4h10M17 4v8a5 5 0 0 1-10 0V4M4 4h3v6H4zM17 4h3v6h-3M9 9h6" />
                  <path d="M12 7l1.5 3 3 .5-2.5 2.5.5 3.5-2.5-1.5-2.5 1.5.5-3.5L7 10.5l3-.5L12 7z" />
                </svg>
              </div>

              {currentData.awards.length > 0 ? (
                <div className="awards-list">
                  {currentData.awards.map((award, index) => (
                    <div className="award-item" key={index}>
                      <h4>{award.org}</h4>
                      <p><span className="dash">—</span> {award.title}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-awards">No awards recorded for this period.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==========================================
// 3. MAIN ABOUT COMPONENT
// ==========================================
const About = () => {
  const [banks, setBanks] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);
  const navigate = useNavigate();

  // Fetch data from API
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/v1/banks-list`)
      .then((res) => res.json())
      .then((json) => {
        if (json.success) {
          // Filtering only active banks with logos
          const activeBanks = json.data.filter(bank => bank.is_active === 1 || bank.is_active === "1" || bank.is_active === true);
          setBanks(activeBanks);
        }
        setLoading(false);
      })
      .catch((err) => console.error("Error fetching banks:", err));
  }, []);

  // Auto Slide Logic
  useEffect(() => {
    if (banks.length === 0) return;
    const interval = setInterval(() => {
      handleNext();
    }, 3000); // Slides every 3 seconds
    return () => clearInterval(interval);
  }, [currentIndex, banks]);

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex + 1 >= banks.length ? 0 : prevIndex + 1
    );
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? banks.length - 1 : prevIndex - 1
    );
  };

  // Safe DOM Manipulation inside useEffect
  useEffect(() => {
    const timelineLinks = document.querySelectorAll('.accolades-nav a');
    const contentItems = document.querySelectorAll('.accolades-content-item');

    const handleLinkClick = (event) => {
      // Prevent the page from jumping
      event.preventDefault();

      // Deactivate all links and content items
      timelineLinks.forEach(l => l.parentNode.classList.remove('active'));
      contentItems.forEach(i => i.classList.remove('active'));

      // Activate the clicked link
      const link = event.currentTarget;
      link.parentNode.classList.add('active');

      // Activate the corresponding content item
      const targetId = link.getAttribute('href').substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.classList.add('active');
      }
    };

    timelineLinks.forEach(link => {
      link.addEventListener('click', handleLinkClick);
    });

    // Cleanup listeners on component unmount
    return () => {
      timelineLinks.forEach(link => {
        link.removeEventListener('click', handleLinkClick);
      });
    };
  }, []);

  return (
    <>
      <div className="about-master-wrapper">
        {/* HERO SECTION */}
        <section className="inspire-banner-section">
          <div className="inspire-container">
            <div className="inspire-content">
              <h4 className="inspire-overline">About Us</h4>
              <h2 className="inspire-subheading">We Started With</h2>
              <h1 className="inspire-main-heading">
                An Inspiration, And Now <br /> We Inspire.
              </h1>
            </div>
          </div>
          <div className="inspire-bg-overlay"></div>
        </section>
      </div>

      {/* FOUNDER MESSAGE SECTION */}
      <section className="rl-founder-section">
        <div className="rl-founder-container">
          <div className="rl-founder-image-wrapper">
            <img src="./images/about/ceo.png" alt="Founder" className="rl-founder-img" />
            <div className="rl-dot-decor decor-1"></div>
            <div className="rl-dot-decor decor-2"></div>
          </div>

          <div className="rl-founder-text">
            <span className="rl-quote-icon">“</span>
            <h2 className="rl-founder-heading">We Empower Our Clients by Giving Right Financial Advice</h2>
            <p className="rl-founder-para">
              "Our value, motto & mission remains to instil the right financial knowledge among our clients
              and bestow the finest services to individuals seeking credit aid. Leveraging upon new-age
              technologies, we endeavour to create a financial environment where we can serve a large
              segment of the population."
            </p>
            <div className="rl-founder-info">
              <strong>Rakesh Panchal</strong>
              <span>Founder of Krishna Finconcept Services</span>
            </div>
          </div>
        </div>
      </section>

      {/* MILESTONES SECTION */}
      <Milestones />

      {/* STATS GRID SECTION */}
      {/* STATS GRID SECTION */}
      <div className="kf-stats-grid">
        {/* ROW 1 */}
        <div className="kf-stats-card kf-bg-purple kf-col-span-2">
          <h3 className="kf-stat-number">2,025</h3>
          <p className="kf-stat-text">We employ 15,000 people around the world</p>
        </div>
        <div className="kf-stats-card kf-card-image-wrapper kf-col-span-1">
          <img src="./images/about/meeting.webp" alt="Colleagues in an office" />
        </div>
        <div className="kf-stats-card kf-bg-blue kf-col-span-2">
          <h3 className="kf-stat-number">16</h3>
          <p className="kf-stat-text">Our colleagues come from 125 different countries</p>
        </div>

        {/* ROW 2 */}
        <div className="kf-stats-card kf-bg-light-purple kf-col-span-1">
          <h3 className="kf-stat-number">75</h3>
          <p className="kf-stat-text">More than 75 years in business</p>
        </div>
        <div className="kf-stats-card kf-bg-pink kf-col-span-1">
          <h3 className="kf-stat-number">449</h3>
          <p className="kf-stat-text">450 branches worldwide</p>
        </div>
        <div className="kf-stats-card kf-bg-light-purple kf-col-span-1">
          <h3 className="kf-stat-number">23</h3>
          <p className="kf-stat-text">We are present in 25 markets</p>
        </div>
        <div className="kf-stats-card kf-bg-overlay-bank kf-col-span-2">
          <div className="kf-overlay-content">
            <h3 className="kf-stat-title">Best Consumer Digital<br />Bank</h3>
            <p className="kf-stat-text">Global Finance 2020</p>
          </div>
        </div>

        {/* ROW 3 */}
        <div className="kf-stats-card kf-bg-overlay-chart kf-col-span-2">
          <div className="kf-overlay-content">
            <h3 className="kf-stat-number">20</h3>
            <p className="kf-stat-text">Global Finance 2020</p>
          </div>
        </div>
        <div className="kf-stats-card kf-card-image-wrapper kf-col-span-1">
          <img src="./images/about/sher.webp" alt="People walking on the street" />
        </div>
        <div className="kf-stats-card kf-bg-purple kf-col-span-2">
          <h3 className="kf-stat-number">1</h3>
          <p className="kf-stat-text">We're listed on two of Asia's largest stock exchanges</p>
        </div>
      </div>

      {/* BANK SLIDER SECTION */}
      {loading ? (
        <div className="bank-loader">Loading Banks...</div>
      ) : (
        <div className="bank-slider-container">
          <div className="bank-slider-header">
            <h2>
              The best offers from <br />
              <span className="accent-text">India’s most trusted banks</span>
            </h2>
            <div className="header-line"></div>
          </div>

          <div className="bank-slider-main">
            <div className="bank-logo-window" ref={sliderRef}>
              <div
                className="bank-logo-track"
                style={{ transform: `translateX(-${currentIndex * (100 / 6)}%)` }}
              >
                {banks.map((bank) => (
                  <div className="bank-logo-item" key={bank.id}>
                    <img
                      src={bank.bank_logo_url}
                      alt={bank.bank_name}
                      title={bank.bank_name}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default About;